import { useQuery } from '@apollo/client';
import { graphql } from '../gql';
import {
  ApplicationQuestionsQuery,
  Question,
  TargetAreaType,
} from '../gql/graphql';
import { NavigationItem } from './types';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { TFunction } from 'i18next';

export const GET_SUBMISSION_APPLICATION_QUESTIONS = graphql(
  `
    query applicationQuestions(
      $filters: QuestionFilterInput
      $pagination: PaginationRequest
      $sort: PaginationSort
    ) {
      nestedQuestions(filters: $filters, pagination: $pagination, sort: $sort) {
        data
      }
    }
  `
);

export function useGetApplicationQuestionsQuery({
  submissionApplicationUuid,
}: {
  submissionApplicationUuid?: string;
}) {
  const { t } = useTranslation();
  const { loading, error, data } = useQuery(
    GET_SUBMISSION_APPLICATION_QUESTIONS,
    {
      variables: {
        filters: {
          targetArea: TargetAreaType.Application,
          targetUuid: submissionApplicationUuid,
        },
      },
      skip: !submissionApplicationUuid,
      fetchPolicy: 'cache-and-network',
    }
  );
  const applicationSections = useMemo(
    () => getApplicationSections(t, data),
    [t, data]
  );
  return {
    loading,
    error,
    data: data,
    applicationSections,
  };
}

const getApplicationSections = (
  t: TFunction<'translation', undefined, 'translation'>,
  data?: ApplicationQuestionsQuery
) => {
  const questions: Question[] = data?.nestedQuestions.data || [];
  const applicationSections = [...questions].reduce<NavigationItem[]>(
    (prev, current) => {
      const find = prev.find((e) => e.value === current.sectionUuid);
      if (!find) {
        prev.push({
          value: current.sectionUuid,
          label: t(
            `application.sections.${current.sectionUuid}`,
            current.section ?? current.sectionUuid
          ),
        });
      }
      return prev;
    },
    []
  );
  return applicationSections;
};
